import { useAuth } from '@kaiizen/kai-pro-api-react-state';
import type { ScanTaskOptimized, WorkStatus } from '@kaiizen/kai-scan-template-system';
import { useCallback, useState } from 'react';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { useScanEditingContextSlice, useToast } from '@/Client/State';
import { useBulkPhotoUploader } from '@/lib/photoLib';

import type { StagedProgressPhoto } from './progressUpdateModalTypes';
import {
  formatProgressUpdateSuccessToast,
  type ProgressUpdateScanNoteMutators,
  submitTaskProgressUpdate,
} from './progressUpdatePostLib';

export const useSubmitProgressUpdate = () => {
  const { scanDataMutators, scanId, scanNoteMutators } = useScanEditingContextSlice([
    'scanDataMutators',
    'scanId',
    'scanNoteMutators',
  ]);
  const { user } = useAuth();
  const uploadPhotos = useBulkPhotoUploader();
  const { showToast, tryWithErrorToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitProgressUpdate = useCallback(async ({
    task,
    groupId,
    selectedStatus,
    note,
    stagedPhotos,
    onSuccess,
  }: {
    task: ScanTaskOptimized;
    groupId: string;
    selectedStatus?: WorkStatus;
    note: string;
    stagedPhotos: StagedProgressPhoto[];
    onSuccess: () => void;
  }): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      const summary = await tryWithErrorToast('post progress update', () =>
        submitTaskProgressUpdate({
          task,
          groupId,
          selectedStatus,
          note,
          photoFiles: stagedPhotos.map(photo => photo.file),
          scanDataMutators,
          scanNoteMutators: scanNoteMutators as ProgressUpdateScanNoteMutators,
          userId: user?.id,
          uploadPhotos,
        }));
      if (!summary) return false;
      trackEvent(ANALYTICS_EVENTS.scanTaskProgressUpdated, {
        scanId,
        groupId,
        taskId: task.id,
        hasNote: summary.hasNote,
        photoCount: summary.photoCount,
        status: summary.selectedStatus,
      });
      showToast(formatProgressUpdateSuccessToast(summary));
      onSuccess();
      return true;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    scanDataMutators,
    scanId,
    scanNoteMutators,
    showToast,
    tryWithErrorToast,
    uploadPhotos,
    user?.id,
  ]);

  return { submitProgressUpdate, isSubmitting };
};
